import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Share2, 
  Sparkles, 
  Eye, 
  Palette, 
  Compass, 
  Layers, 
  Check, 
  ExternalLink,
  PhoneCall,
  Building2,
  Award
} from 'lucide-react';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import TopSuggestions from '../Components/TopSuggestions';
import posterImage from '../Images/forms_of_togetherness.jpg';

const ArtShows = () => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Art Shows | Forms of Togetherness by Romesh Mistry - VIZAURA";
  }, []);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Forms of Togetherness - 16th Solo Show by Romesh Mistry",
        text: "Join us for 'Forms of Togetherness' by Romesh Mistry at The Art Gallery, Amdavad Ni Gufa (14-18 October, 4 PM - 8 PM).",
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const createGoogleCalendarUrl = () => {
    const title = encodeURIComponent("Forms of Togetherness - 16th Solo Show by Romesh Mistry");
    const details = encodeURIComponent(
      "Forms of Togetherness explores the connections that bring us together through abstract forms, colours, and geometric elements.\n\nInauguration: 13 Oct 6 PM by Shri Manil Patel (Principal Architect, MMP Architects)\nPublic Viewing: 14 - 18 October, 4 PM to 8 PM\nPresented in association with VIZAURA."
    );
    const location = encodeURIComponent("The Art Gallery, Amdavad Ni Gufa, CEPT Campus, Ahmedabad, Gujarat");
    const dates = "20261014T103000Z/20261018T143000Z";
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&location=${location}&dates=${dates}`;
  };

  return (
    <div className="bg-[#f8f9fa] min-h-screen font-serif text-gray-800 selection:bg-yellow-100 selection:text-yellow-900 w-full overflow-x-hidden">
      <Navbar />

      {/* ========================================================================= */}
      {/* 1. UPCOMING SHOW - HERO SECTION (MOBILE OPTIMIZED) */}
      {/* ========================================================================= */}
      <section className="relative pt-24 sm:pt-28 pb-10 sm:pb-16 px-4 sm:px-6 lg:px-12 w-full overflow-hidden border-b border-gray-200/80">
        {/* Subtle decorative background gradient */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-6xl h-80 bg-gradient-to-b from-yellow-50/70 via-gray-100/40 to-transparent -z-10 pointer-events-none rounded-full blur-3xl opacity-90" />

        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            
            {/* Slide 1 Label Badge */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] sm:text-xs font-sans font-semibold tracking-wider text-yellow-800 bg-yellow-100/80 border border-yellow-200/70 uppercase mb-3 sm:mb-4 shadow-xs"
            >
              <Sparkles className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
              UPCOMING SHOW • SOLO EXHIBITION
            </motion.div>

            {/* Invitation Header */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-[11px] sm:text-xs md:text-sm font-sans tracking-[0.2em] sm:tracking-[0.25em] uppercase text-gray-500 mb-2"
            >
              An Invitation to Experience
            </motion.p>

            {/* Main Exhibition Title */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-tight mb-2 sm:mb-3"
            >
              FORMS OF <span className="text-yellow-600 italic font-serif">TOGETHERNESS</span>
            </motion.h1>

            {/* Subtitle / Artist */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="space-y-0.5 mb-5 sm:mb-6"
            >
              <p className="text-[11px] sm:text-xs font-sans tracking-[0.2em] text-gray-500 uppercase font-semibold">
                16th Solo Show By
              </p>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-900 font-serif font-medium tracking-wide">
                ROMESH MISTRY
              </p>
            </motion.div>

            {/* Key Quick Highlight Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="inline-flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 bg-white px-4 sm:px-5 py-3 rounded-2xl border border-gray-200 shadow-xs mb-6 sm:mb-8 text-xs sm:text-sm font-sans w-full sm:w-auto"
            >
              <div className="flex items-center gap-2 text-gray-800">
                <Calendar className="w-4 h-4 text-yellow-600 shrink-0" />
                <span className="font-semibold">14 - 18 October &apos;26</span>
              </div>
              <div className="hidden sm:block w-px h-3.5 bg-gray-200" />
              <div className="flex items-center gap-2 text-gray-800">
                <Clock className="w-4 h-4 text-yellow-600 shrink-0" />
                <span>4:00 PM – 8:00 PM</span>
              </div>
              <div className="hidden sm:block w-px h-3.5 bg-gray-200" />
              <div className="flex items-center gap-2 text-gray-800 text-center">
                <MapPin className="w-4 h-4 text-yellow-600 shrink-0" />
                <span className="font-medium">The Art Gallery, Amdavad Ni Gufa</span>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 sm:gap-3 max-w-sm sm:max-w-none mx-auto"
            >
              <a
                href={createGoogleCalendarUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 bg-gray-900 hover:bg-yellow-600 text-white rounded-xl font-sans text-xs sm:text-sm font-medium tracking-wide transition-all duration-300 flex items-center justify-center gap-2 shadow-xs hover:shadow-md transform hover:-translate-y-0.5 active:scale-98"
              >
                <Calendar className="w-4 h-4 text-yellow-400 shrink-0" />
                Add to Calendar
              </a>

              <a
                href="https://maps.google.com/?q=Amdavad+Ni+Gufa+Ahmedabad"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 rounded-xl font-sans text-xs sm:text-sm font-medium tracking-wide transition-all duration-300 flex items-center justify-center gap-2 shadow-2xs active:scale-98"
              >
                <MapPin className="w-4 h-4 text-yellow-600 shrink-0" />
                Get Directions
              </a>

              <button
                onClick={handleShare}
                className="px-4 py-2.5 bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 rounded-xl font-sans text-xs sm:text-sm font-medium transition-all duration-300 flex items-center justify-center gap-1.5 shadow-2xs active:scale-98"
                title="Share Invitation"
              >
                {copied ? <Check className="w-4 h-4 text-green-600 shrink-0" /> : <Share2 className="w-4 h-4 text-yellow-600 shrink-0" />}
                <span>{copied ? "Link Copied!" : "Share"}</span>
              </button>
            </motion.div>

          </div>
        </div>
      </section>


      {/* ========================================================================= */}
      {/* 2. DATA FROM IMAGE - INVITATION CARD & EXHIBITION DETAILS */}
      {/* ========================================================================= */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 lg:px-12 w-full bg-white">
        <div className="w-full max-w-6xl mx-auto">
          
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <span className="text-[11px] sm:text-xs font-sans font-semibold tracking-widest text-yellow-600 uppercase">
              Official Invitation
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
              The Invitation Card
            </h2>
            <div className="w-16 h-0.5 bg-yellow-600 mx-auto mt-2.5" />
            <p className="text-xs sm:text-sm text-gray-600 font-sans mt-2">
              Presented in honor of contemporary visual arts and architectural harmony.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            
            {/* Left: Beautifully Rendered Invitation Card */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 flex justify-center w-full"
            >
              <div className="relative w-full max-w-md bg-[#FAF5ED] rounded-2xl p-5 sm:p-8 shadow-md border border-[#E2D4BF] text-center overflow-hidden transition-transform duration-500 hover:scale-[1.01]">
                
                {/* Decorative Geometric Corner Art Ornaments */}
                <div className="absolute top-0 left-0 w-16 sm:w-20 h-28 sm:h-32 pointer-events-none opacity-80 sm:opacity-90">
                  <div className="w-4 sm:w-5 h-8 sm:h-10 bg-[#3B5B72] rounded-r-md mt-1"></div>
                  <div className="w-7 sm:w-8 h-7 sm:h-8 bg-[#D48238] rounded-br-lg -mt-1 ml-1"></div>
                  <div className="w-5 sm:w-6 h-10 sm:h-12 bg-[#B95D26] rounded-tr-md mt-1"></div>
                </div>

                <div className="absolute bottom-0 right-0 w-16 sm:w-20 h-32 sm:h-36 pointer-events-none opacity-80 sm:opacity-90 flex flex-col items-end">
                  <div className="w-5 sm:w-6 h-10 sm:h-12 bg-[#C25E23] rounded-tl-md"></div>
                  <div className="w-8 sm:w-10 h-7 sm:h-8 bg-[#E09D3E] rounded-l-md -mt-1 mr-1"></div>
                  <div className="w-4 sm:w-5 h-8 sm:h-10 bg-[#2D4558] rounded-l-md mt-1"></div>
                </div>

                {/* Inner Content matching image text strictly */}
                <div className="relative z-10 py-2 px-1 sm:px-2 space-y-3.5 sm:space-y-4">
                  
                  <div>
                    <p className="text-[10px] sm:text-xs font-sans tracking-[0.2em] sm:tracking-[0.3em] text-[#6E5D4C] uppercase font-medium">
                      AN INVITATION TO EXPERIENCE
                    </p>
                  </div>

                  <div className="pt-1">
                    <h3 className="text-xl sm:text-3xl font-bold tracking-wide text-[#1C2833] font-serif leading-tight">
                      FORMS OF<br />TOGETHERNESS
                    </h3>
                    
                    {/* Diamond Separator */}
                    <div className="flex items-center justify-center gap-2 sm:gap-2.5 my-2.5 sm:my-3">
                      <div className="w-8 sm:w-10 h-[1px] bg-[#B09A7B]"></div>
                      <div className="w-1.5 h-1.5 rotate-45 bg-[#B09A7B]"></div>
                      <div className="w-8 sm:w-10 h-[1px] bg-[#B09A7B]"></div>
                    </div>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-[10px] sm:text-[11px] font-sans tracking-[0.2em] text-[#7A644D] uppercase font-semibold">
                      16TH SOLO SHOW BY
                    </p>
                    <p className="text-base sm:text-xl font-bold tracking-wider text-[#C25E23] font-serif">
                      ROMESH MISTRY
                    </p>
                  </div>

                  {/* Horizontal Bar */}
                  <div className="w-10 h-[1px] bg-[#C5B398] mx-auto my-2 sm:my-2.5"></div>

                  {/* Inauguration Block */}
                  <div className="bg-[#F0E6D2]/70 rounded-xl p-2.5 sm:p-3 border border-[#DECDB0] space-y-1">
                    <p className="text-[11px] sm:text-xs font-sans text-[#4A3B2C] font-medium">
                      Inauguration on
                    </p>
                    <p className="text-sm sm:text-base font-bold text-[#1C2833] font-serif tracking-wide">
                      13 <span className="text-[10px] uppercase font-sans">th</span> OCTOBER <span className="text-[#C25E23]">6 PM</span>
                    </p>
                    <p className="text-xs sm:text-sm font-sans text-[#4A3B2C] pt-0.5">
                      by <strong className="text-[#1C2833] font-serif">Shri MANIL PATEL</strong>
                    </p>
                    <p className="text-[10px] sm:text-xs italic text-[#6E5D4C] font-serif">
                      Principal Architect, MMP Architects
                    </p>
                  </div>

                  {/* Horizontal Bar */}
                  <div className="w-10 h-[1px] bg-[#C5B398] mx-auto my-2 sm:my-2.5"></div>

                  {/* Date & Timings */}
                  <div className="space-y-0.5">
                    <p className="text-sm sm:text-base font-bold text-[#1C2833] font-serif tracking-wider">
                      14 - 18 OCTOBER &apos;26
                    </p>
                    <p className="text-xs sm:text-sm font-medium font-sans text-[#7A5A38]">
                      4 pm to 8 pm
                    </p>
                  </div>

                  {/* Horizontal Bar */}
                  <div className="w-10 h-[1px] bg-[#C5B398] mx-auto my-2 sm:my-2.5"></div>

                  {/* Venue Block */}
                  <div className="pt-0.5">
                    <p className="text-[10px] sm:text-xs font-sans tracking-[0.15em] sm:tracking-[0.2em] font-bold text-[#9C4B18] uppercase">
                      THE ART GALLERY
                    </p>
                    <p className="text-sm sm:text-lg font-bold tracking-widest text-[#9C4B18] font-serif uppercase">
                      AMDAVAD NI GUFA
                    </p>
                  </div>

                </div>
              </div>
            </motion.div>

            {/* Right: Detailed Event Overview & Breakdown */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 space-y-3 sm:space-y-4"
            >
              <div className="bg-[#f8f9fa] rounded-2xl p-4 sm:p-7 border border-gray-200/80 shadow-xs space-y-3.5 sm:space-y-4">
                
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 font-serif flex items-center gap-2">
                  <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-600 shrink-0" />
                  Exhibition Overview
                </h3>

                <p className="text-gray-600 font-sans leading-relaxed text-xs sm:text-sm">
                  VIZAURA proudly presents <strong>Forms of Togetherness</strong>, the landmark 16th solo art show by eminent artist <strong>Romesh Mistry</strong>. This curated solo exhibition brings together an exceptional body of work exploring the intricate relationships between human connection, architectural space, and abstract geometry.
                </p>

                <div className="space-y-2.5 sm:space-y-3 pt-1">
                  
                  {/* Event item 1: Inauguration */}
                  <div className="flex items-start gap-3 p-3 sm:p-3.5 rounded-xl bg-white border border-gray-200 shadow-2xs">
                    <div className="p-2 rounded-lg bg-yellow-600 text-white shrink-0 mt-0.5">
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-[11px] font-sans font-semibold tracking-wider text-yellow-700 uppercase block">
                        Ceremonial Opening
                      </span>
                      <h4 className="text-xs sm:text-base font-bold text-gray-900 font-serif">
                        Grand Inauguration • 13th October, 6:00 PM
                      </h4>
                      <p className="text-xs text-gray-600 font-sans mt-0.5">
                        Inaugurated by <strong>Shri Manil Patel</strong>, Principal Architect at MMP Architects.
                      </p>
                    </div>
                  </div>

                  {/* Event item 2: Public Viewing */}
                  <div className="flex items-start gap-3 p-3 sm:p-3.5 rounded-xl bg-white border border-gray-200 shadow-2xs">
                    <div className="p-2 rounded-lg bg-gray-900 text-white shrink-0 mt-0.5">
                      <Eye className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-[11px] font-sans font-semibold tracking-wider text-gray-900 uppercase block">
                        Public Gallery Viewing
                      </span>
                      <h4 className="text-xs sm:text-base font-bold text-gray-900 font-serif">
                        14th – 18th October &apos;26 • 4:00 PM to 8:00 PM
                      </h4>
                      <p className="text-xs text-gray-600 font-sans mt-0.5">
                        Open to art connoisseurs, architects, interior designers, and collectors. Free entry.
                      </p>
                    </div>
                  </div>

                  {/* Event item 3: Venue Info */}
                  <div className="flex items-start gap-3 p-3 sm:p-3.5 rounded-xl bg-white border border-gray-200 shadow-2xs">
                    <div className="p-2 rounded-lg bg-yellow-700 text-white shrink-0 mt-0.5">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] sm:text-[11px] font-sans font-semibold tracking-wider text-yellow-800 uppercase block">
                        Iconic Gallery Venue
                      </span>
                      <h4 className="text-xs sm:text-base font-bold text-gray-900 font-serif">
                        The Art Gallery, Amdavad Ni Gufa
                      </h4>
                      <p className="text-xs text-gray-600 font-sans mt-0.5">
                        Kasturbhai Lalbhai Campus, CEPT University, Ahmedabad 380009.
                      </p>
                      <a
                        href="https://maps.google.com/?q=Amdavad+Ni+Gufa+Ahmedabad"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-sans font-semibold text-yellow-600 hover:text-yellow-700 hover:underline mt-1"
                      >
                        Open in Google Maps <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>

                </div>

              </div>
            </motion.div>

          </div>

        </div>
      </section>


      {/* ========================================================================= */}
      {/* 3. ABOUT THE SHOW SECTION */}
      {/* ========================================================================= */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 lg:px-12 w-full bg-[#f8f9fa] border-y border-gray-200/80">
        <div className="w-full max-w-6xl mx-auto">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-center">
            
            {/* Visual Artwork Feature */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5 w-full"
            >
              <div className="relative group max-w-xs sm:max-w-sm mx-auto lg:max-w-none">
                <div className="bg-white p-2.5 sm:p-4 rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <img
                    src={posterImage}
                    alt="Forms of Togetherness Exhibition Artwork"
                    className="w-full h-auto object-cover rounded-xl shadow-2xs transform transition-transform duration-700 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                  <div className="mt-2.5 px-1 flex items-center justify-between text-[10px] sm:text-[11px] font-sans text-gray-500">
                    <span>Featured Series • Mixed Media</span>
                    <span className="font-serif italic font-semibold text-yellow-700">Forms of Togetherness</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Curatorial Text & Thematic Pillars */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 space-y-3.5 sm:space-y-4"
            >
              
              <div>
                <span className="text-[11px] sm:text-xs font-sans font-semibold tracking-widest text-yellow-600 uppercase">
                  Curatorial Statement
                </span>
                <h2 className="text-xl sm:text-3xl font-bold text-gray-900 font-serif mt-1 mb-1.5 sm:mb-2 leading-tight">
                  About The Show
                </h2>
                <div className="w-14 h-0.5 bg-yellow-600 mb-3 sm:mb-4 rounded-full" />
              </div>

              {/* Exact user requested quote / concept block */}
              <div className="p-4 sm:p-6 rounded-xl bg-white border-l-3 border-yellow-600 border border-gray-200 shadow-2xs">
                <p className="text-xs sm:text-base text-gray-800 font-serif italic leading-relaxed">
                  &ldquo;Forms of Togetherness explores the connections that bring us together through abstract forms, colours, and geometric elements. Celebrating individuality within a collective whole, the series reflects on how differences coexist, creating harmony, connection, and a shared sense of belonging.&rdquo;
                </p>
                <p className="text-right text-[11px] sm:text-xs font-sans font-semibold text-yellow-700 mt-2 uppercase tracking-wider">
                  — Romesh Mistry
                </p>
              </div>

              <div className="text-xs sm:text-sm text-gray-600 font-sans leading-relaxed space-y-2 sm:space-y-2.5">
                <p>
                  In this landmark 16th solo showcase, the canvases become a dialogue between balance and tension. By layering earthen pigments, terracotta washes, ochres, and deep indigos, Mistry crafts an ecosystem where each distinct geometric shape retains its unique identity while locking into an inseparable, harmonious composition.
                </p>
                <p>
                  The exhibition invites viewers to step back and reflect upon modern human interdependence — how disparate emotions, personalities, and memories blend into unified experiences of warmth and coexistence.
                </p>
              </div>

              {/* Pillars Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 pt-1 sm:pt-2">
                <div className="p-3 sm:p-3.5 rounded-xl bg-white border border-gray-200 shadow-2xs">
                  <Palette className="w-4 h-4 text-yellow-600 mb-1" />
                  <h4 className="font-serif font-bold text-gray-900 text-xs sm:text-sm">Geometric Balance</h4>
                  <p className="text-[10px] sm:text-[11px] text-gray-600 font-sans mt-0.5">Interlocking shapes reflecting coexistence.</p>
                </div>

                <div className="p-3 sm:p-3.5 rounded-xl bg-white border border-gray-200 shadow-2xs">
                  <Layers className="w-4 h-4 text-yellow-600 mb-1" />
                  <h4 className="font-serif font-bold text-gray-900 text-xs sm:text-sm">Earthy Pigments</h4>
                  <p className="text-[10px] sm:text-[11px] text-gray-600 font-sans mt-0.5">Rich terracotta and ochre textures.</p>
                </div>

                <div className="p-3 sm:p-3.5 rounded-xl bg-white border border-gray-200 shadow-2xs">
                  <Compass className="w-4 h-4 text-yellow-600 mb-1" />
                  <h4 className="font-serif font-bold text-gray-900 text-xs sm:text-sm">Spatial Rhythm</h4>
                  <p className="text-[10px] sm:text-[11px] text-gray-600 font-sans mt-0.5">Harmonious resonance for architecture.</p>
                </div>
              </div>

            </motion.div>

          </div>

        </div>
      </section>


      {/* ========================================================================= */}
      {/* 4. DATES, TIMINGS & VENUE SPOTLIGHT */}
      {/* ========================================================================= */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 lg:px-12 w-full bg-white">
        <div className="w-full max-w-6xl mx-auto">
          
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <span className="text-[11px] sm:text-xs font-sans font-semibold tracking-widest text-yellow-600 uppercase">
              Plan Your Visit
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
              Dates, Timings & Venue
            </h2>
            <div className="w-16 h-0.5 bg-yellow-600 mx-auto mt-2.5" />
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            
            {/* Card 1: Inauguration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#f8f9fa] rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-xs hover:shadow-md transition-shadow flex flex-col justify-between"
            >
              <div>
                <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-yellow-100/80 border border-yellow-200/80 flex items-center justify-center text-yellow-700 mb-3 sm:mb-4">
                  <Sparkles className="w-4 sm:w-5 h-4 sm:h-5" />
                </div>
                <span className="text-[10px] sm:text-[11px] font-sans font-semibold tracking-wider text-yellow-700 uppercase">
                  Inauguration Evening
                </span>
                <h3 className="text-base sm:text-xl font-bold text-gray-900 font-serif mt-0.5 sm:mt-1 mb-1.5 sm:mb-2">
                  13th October &apos;26
                </h3>
                <p className="text-xs sm:text-sm font-sans text-gray-600 leading-relaxed mb-3 sm:mb-4">
                  Grand opening ceremony commencing at <strong>6:00 PM</strong>. Presided over by <strong>Shri Manil Patel</strong> (Principal Architect, MMP Architects).
                </p>
              </div>
              <div className="pt-2.5 sm:pt-3 border-t border-gray-200 flex items-center justify-between text-xs font-sans text-gray-500">
                <span>Opening Reception</span>
                <span className="font-semibold text-yellow-700">6:00 PM onwards</span>
              </div>
            </motion.div>

            {/* Card 2: Public Exhibition Dates */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-gray-900 text-white rounded-2xl p-4 sm:p-6 shadow-md flex flex-col justify-between relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full blur-2xl pointer-events-none" />
              <div>
                <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-yellow-400 mb-3 sm:mb-4">
                  <Calendar className="w-4 sm:w-5 h-4 sm:h-5" />
                </div>
                <span className="text-[10px] sm:text-[11px] font-sans font-semibold tracking-wider text-yellow-400 uppercase">
                  Public Viewing Period
                </span>
                <h3 className="text-base sm:text-xl font-bold text-white font-serif mt-0.5 sm:mt-1 mb-1.5 sm:mb-2">
                  14 – 18 October
                </h3>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 text-xs font-sans font-medium text-amber-200 mb-3 sm:mb-4">
                  <Clock className="w-3.5 h-3.5" /> 4:00 PM to 8:00 PM Daily
                </div>
                <p className="text-xs sm:text-sm font-sans text-gray-300 leading-relaxed">
                  Five days of curated public exhibition, artist interactions, and architectural dialogue at Amdavad Ni Gufa.
                </p>
              </div>
              <div className="pt-2.5 sm:pt-3 border-t border-white/10 flex items-center justify-between text-xs font-sans text-gray-300">
                <span>Admission: Free & Open</span>
                <span className="text-amber-300 font-semibold">5 Days</span>
              </div>
            </motion.div>

            {/* Card 3: Venue Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-[#f8f9fa] rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-xs hover:shadow-md transition-shadow flex flex-col justify-between"
            >
              <div>
                <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-yellow-100/80 border border-yellow-200/80 flex items-center justify-center text-yellow-700 mb-3 sm:mb-4">
                  <MapPin className="w-4 sm:w-5 h-4 sm:h-5" />
                </div>
                <span className="text-[10px] sm:text-[11px] font-sans font-semibold tracking-wider text-yellow-700 uppercase">
                  Exhibition Space
                </span>
                <h3 className="text-base sm:text-xl font-bold text-gray-900 font-serif mt-0.5 sm:mt-1 mb-1">
                  The Art Gallery
                </h3>
                <p className="text-xs sm:text-sm font-semibold text-yellow-600 font-serif mb-1.5 sm:mb-2">
                  Amdavad Ni Gufa
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Kasturbhai Lalbhai Campus, CEPT University, University Area, Ahmedabad, Gujarat 380009.
                </p>
              </div>
              <div className="pt-2.5 sm:pt-3 border-t border-gray-200 flex items-center justify-between">
                <a
                  href="https://maps.google.com/?q=Amdavad+Ni+Gufa+Ahmedabad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-sans font-semibold text-yellow-600 hover:text-yellow-700 hover:underline inline-flex items-center gap-1"
                >
                  Get Directions <ExternalLink className="w-3 h-3" />
                </a>
                <span className="text-xs font-sans text-gray-500">Ahmedabad</span>
              </div>
            </motion.div>

          </div>

        </div>
      </section>


      {/* ========================================================================= */}
      {/* 5. ABOUT THE ARTIST */}
      {/* ========================================================================= */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 lg:px-12 w-full bg-[#f8f9fa] border-y border-gray-200/80">
        <div className="w-full max-w-6xl mx-auto">
          
          <div className="bg-white rounded-3xl p-5 sm:p-10 border border-gray-200/80 shadow-md">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-center">
              
              {/* Left Column: Artist Milestone Seal & Highlights */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-5 flex flex-col justify-center w-full"
              >
                <div className="bg-gradient-to-br from-[#FAF5ED] to-[#F3ECE0] rounded-2xl p-5 sm:p-8 border border-[#E2D4BF] text-center space-y-3.5 sm:space-y-4 shadow-2xs">
                  
                  <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-yellow-600 text-white flex items-center justify-center mx-auto shadow-md">
                    <Award className="w-6 sm:w-8 h-6 sm:h-8" />
                  </div>

                  <div>
                    <span className="text-[10px] sm:text-xs font-sans font-semibold tracking-widest text-[#8C6D4F] uppercase">
                      Featured Artist
                    </span>
                    <h3 className="text-xl sm:text-3xl font-bold font-serif text-gray-900 mt-0.5 sm:mt-1">
                      Romesh Mistry
                    </h3>
                    <p className="text-[11px] sm:text-xs font-sans text-yellow-700 font-semibold uppercase tracking-wider mt-1">
                      Visual Artist & Master Colorist
                    </p>
                  </div>

                  <div className="w-12 h-0.5 bg-[#C5B398] mx-auto"></div>

                  <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-1">
                    <div className="p-2.5 sm:p-3 bg-white/80 rounded-xl border border-[#DECDB0] text-center">
                      <span className="block text-base sm:text-lg font-bold text-gray-900 font-sans">16th</span>
                      <span className="text-[10px] sm:text-[11px] text-gray-600 font-sans">Solo Show</span>
                    </div>
                    <div className="p-2.5 sm:p-3 bg-white/80 rounded-xl border border-[#DECDB0] text-center">
                      <span className="block text-base sm:text-lg font-bold text-gray-900 font-sans">Abstract</span>
                      <span className="text-[10px] sm:text-[11px] text-gray-600 font-sans">Mastery</span>
                    </div>
                  </div>

                  <p className="text-[11px] sm:text-xs text-gray-600 font-sans italic leading-relaxed pt-0.5">
                    &ldquo;Harmonizing organic textures and architectural geometry into unified visual balance.&rdquo;
                  </p>
                </div>
              </motion.div>

              {/* Right Column: Artist Bio & Statement */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-7 space-y-3.5 sm:space-y-4"
              >
                
                <div>
                  <span className="text-[11px] sm:text-xs font-sans font-semibold tracking-widest text-yellow-600 uppercase">
                    Artist Profile
                  </span>
                  <h2 className="text-xl sm:text-3xl font-bold text-gray-900 font-serif mt-1 mb-1.5 sm:mb-2">
                    About the Artist
                  </h2>
                  <div className="w-14 h-0.5 bg-yellow-600 mb-3 sm:mb-4 rounded-full" />
                </div>

                <p className="text-xs sm:text-base text-gray-700 font-sans leading-relaxed">
                  <strong>Romesh Mistry</strong> is an accomplished visual artist whose work seamlessly bridges the expressive freedom of abstract painting with the structured discipline of spatial and geometric design.
                </p>

                <p className="text-xs sm:text-sm text-gray-600 font-sans leading-relaxed">
                  Over decades of dedicated studio practice, Romesh has developed an unmistakable visual lexicon defined by warm, earthy tones, tactile surface textures, and nuanced geometric equilibrium. His works celebrate the quiet strength found in organic materials, earthen pigments, and structured forms.
                </p>

                <p className="text-xs sm:text-sm text-gray-600 font-sans leading-relaxed">
                  Marking his <strong>16th Solo Exhibition</strong>, <em>Forms of Togetherness</em> represents a career milestone — a deeply contemplative body of art celebrating human connections, community, and the timeless harmony that emerges when diverse elements unite.
                </p>

                <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3">
                  <a
                    href="https://wa.me/919016663115?text=Hello%20Vizaura%2C%20I%20would%20like%20to%20inquire%20about%20Romesh%20Mistry's%20artworks%20from%20the%20Forms%20of%20Togetherness%20show."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 rounded-xl bg-yellow-600 hover:bg-yellow-700 text-white font-sans text-xs sm:text-sm font-medium transition-colors shadow-xs flex items-center justify-center gap-2 active:scale-98"
                  >
                    <PhoneCall className="w-4 h-4" />
                    Inquire About Artworks
                  </a>

                  <a
                    href="https://maps.google.com/?q=Amdavad+Ni+Gufa+Ahmedabad"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 rounded-xl bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 font-sans text-xs sm:text-sm font-medium transition-colors shadow-2xs flex items-center justify-center gap-1.5 active:scale-98"
                  >
                    <MapPin className="w-4 h-4 text-yellow-600" />
                    Visit Gufa Gallery
                  </a>
                </div>

              </motion.div>

            </div>
          </div>

        </div>
      </section>


      {/* ========================================================================= */}
      {/* 6. OTHER CATEGORIES OF VIZAURA */}
      {/* ========================================================================= */}
      <section className="bg-white w-full">
        <TopSuggestions />
      </section>

      <Footer />
    </div>
  );
};

export default ArtShows;
